(() => {
  if (customElements.get('mt-newsletter-popup')) return;

  class MtNewsletterPopup extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.dialog = this.querySelector('[data-newsletter-dialog]');
      this.consent = this.querySelector('[data-newsletter-consent]');
      this.submit = this.querySelector('[data-newsletter-submit]');
      this.delay = Number.parseInt(this.dataset.delay || '0', 10);
      this.sectionId = this.dataset.sectionId || '';
      this.isDesignMode = Boolean(window.Shopify?.designMode);
      this.closeButtons = Array.from(this.querySelectorAll('[data-newsletter-close]'));

      this.handleCloseClick = this.handleCloseClick.bind(this);
      this.handleConsentChange = this.handleConsentChange.bind(this);
      this.handleDialogClick = this.handleDialogClick.bind(this);
      this.handleDialogClose = this.handleDialogClose.bind(this);
      this.handleViewportChange = this.handleViewportChange.bind(this);
      this.handleSectionSelect = this.handleSectionSelect.bind(this);
      this.handleSectionDeselect = this.handleSectionDeselect.bind(this);

      this.closeButtons.forEach((button) => button.addEventListener('click', this.handleCloseClick));
      this.consent?.addEventListener('change', this.handleConsentChange);
      this.dialog?.addEventListener('click', this.handleDialogClick);
      this.dialog?.addEventListener('close', this.handleDialogClose);
      window.addEventListener('resize', this.handleViewportChange, { passive: true });
      document.addEventListener('shopify:section:select', this.handleSectionSelect);
      document.addEventListener('shopify:section:deselect', this.handleSectionDeselect);

      this.prepareMedia();
      this.syncDialogOffset();
      this.handleConsentChange();

      const formHasFeedback = Boolean(
        this.querySelector('[data-newsletter-success], [data-newsletter-error]')
      );

      if (this.querySelector('[data-newsletter-success]')) {
        this.writeStorage('theme:popup-filled', 'true');
      }

      if (this.isDesignMode || formHasFeedback) {
        this.open();
      } else if (this.shouldAppearAutomatically()) {
        this.timer = window.setTimeout(() => this.open(), Math.max(0, this.delay));
      }
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      this.closeButtons?.forEach((button) => button.removeEventListener('click', this.handleCloseClick));
      this.consent?.removeEventListener('change', this.handleConsentChange);
      this.dialog?.removeEventListener('click', this.handleDialogClick);
      this.dialog?.removeEventListener('close', this.handleDialogClose);
      window.removeEventListener('resize', this.handleViewportChange);
      document.removeEventListener('shopify:section:select', this.handleSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.handleSectionDeselect);
      this.initialized = false;
    }

    shouldAppearAutomatically() {
      if (this.readStorage('theme:popup-filled') === 'true') return false;
      if (this.hasAttribute('data-show-only-once') && this.readStorage('theme:popup-appeared') === 'true') {
        return false;
      }
      return !document.cookie.split('; ').some((item) => item.startsWith('newsletter_cookie='));
    }

    open() {
      if (!this.dialog || this.dialog.open) return;
      window.clearTimeout(this.timer);
      this.syncDialogOffset();
      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
        this.dialog.setAttribute('aria-modal', 'true');
      }
      if (!this.isDesignMode && this.hasAttribute('data-show-only-once')) {
        this.writeStorage('theme:popup-appeared', 'true');
      }
      window.requestAnimationFrame(() => {
        const focusTarget = this.querySelector('[data-newsletter-success]')
          ? this.querySelector('[data-newsletter-close]')
          : this.dialog;
        focusTarget?.focus({ preventScroll: true });
      });
    }

    close() {
      if (!this.dialog?.open) return;
      if (typeof this.dialog.close === 'function') this.dialog.close('dismissed');
      else {
        this.dialog.removeAttribute('open');
        this.dialog.removeAttribute('aria-modal');
        this.rememberDismissal();
      }
    }

    handleCloseClick() {
      this.close();
    }

    handleConsentChange() {
      if (!this.submit) return;
      this.submit.disabled = Boolean(this.consent && !this.consent.checked);
    }

    handleDialogClick(event) {
      if (event.target === this.dialog) this.close();
    }

    handleDialogClose() {
      if (!this.isDesignMode) this.rememberDismissal();
    }

    handleViewportChange() {
      if (this.dialog?.open) this.syncDialogOffset();
    }

    prepareMedia() {
      this.querySelectorAll('.mt-newsletter-popup__media img').forEach((image) => {
        if (image.complete) return;
        image.loading = 'eager';
        image.fetchPriority = 'low';
      });
    }

    syncDialogOffset() {
      let headerBottom = 0;

      document
        .querySelectorAll('.mt-announcement-bar, .mt-transparent-navbar__bar, [data-header]')
        .forEach((candidate) => {
          const rect = candidate.getBoundingClientRect();
          if (rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight) {
            headerBottom = Math.max(headerBottom, rect.bottom);
          }
        });

      const maximumOffset = Math.max(0, window.innerHeight - 240);
      const offset = Math.min(maximumOffset, Math.max(0, Math.round(headerBottom)));
      this.style.setProperty('--mt-newsletter-popup-top-offset', `${offset}px`);
    }

    handleSectionSelect(event) {
      if (event.detail?.sectionId === this.sectionId) this.open();
    }

    handleSectionDeselect(event) {
      if (event.detail?.sectionId === this.sectionId && this.dialog?.open) this.dialog.close();
    }

    rememberDismissal() {
      const expires = new Date(Date.now() + 24 * 60 * 60 * 1000).toUTCString();
      document.cookie = `newsletter_cookie=newsletter_cookie; expires=${expires}; path=/; SameSite=Lax`;
    }

    readStorage(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (_error) {
        return null;
      }
    }

    writeStorage(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch (_error) {
        // Storage can be unavailable in privacy modes; the popup remains functional.
      }
    }
  }

  customElements.define('mt-newsletter-popup', MtNewsletterPopup);
})();
